// Bilingual content for the Privacy Policy page (src/components/Privacy.tsx)

export interface PrivacyLabeledItem {
  label: string;
  text: string;
}

export interface PrivacyPolicyLink {
  text: string;
  href: string;
}

export interface PrivacyContent {
  backLabel: string;
  pageTitle: string;
  lastUpdatedLabel: string;
  lastUpdatedDate: string;

  // Section 1 — Introduction
  s1Title: string;
  s1P1: string;
  controllerLabel: string;
  controllerValue: string;
  emailLabel: string;
  websiteLabel: string;

  // Section 2 — Données collectées via Google
  s2Title: string;
  s2_1Title: string;
  s2_1P1: string;
  s2_1List: string[];
  limitedScopeLabel: string;
  limitedScopeText: string;
  s2_2Title: string;
  s2_2IntroPre: string;
  s2_2IntroBold: string;
  s2_2List: string[];

  // Section 3 — Utilisation des données
  s3Title: string;
  s3_1Title: string;
  authLabel: string;
  authList: string[];
  progressLabel: string;
  progressText: string;
  communityLabel: string;
  communityText: string;
  s3_2Title: string;
  legalBasisList: string[];
  s3_3Title: string;
  retentionList: PrivacyLabeledItem[];

  // Section 4 — Partage des données
  s4Title: string;
  s4_1Title: string;
  subprocessorSupabaseName: string;
  subprocessorSupabaseText: string;
  subprocessorGoogleName: string;
  subprocessorGoogleText1: string;
  subprocessorGoogleAdSenseBold: string;
  subprocessorGoogleText2: string;
  subprocessorGoogleLinkText: string;
  subprocessorStripeName: string;
  subprocessorStripeText: string;
  subprocessorVercelName: string;
  subprocessorVercelText: string;
  noCommercialLabel: string;
  noCommercialText: string;
  s4_2Title: string;
  s4_2Intro: string;
  mandatorySharingList: string[];

  // Section 5 — Sécurité des données
  s5Title: string;
  s5List: string[];

  // Section 6 — Suppression des données
  s6Title: string;
  s6P1: string;
  s6P2: string;
  s6Steps: string[];
  s6NotePre: string;
  s6NoteBold: string;

  // Section 7 — Données d'application collectées
  s7Title: string;
  s7_1Title: string;
  s7_1List: string[];
  s7_2Title: string;
  s7_2List: string[];
  s7_3Title: string;
  s7_3Item1: string;
  adsenseCookiesBold: string;
  adsenseCookiesText: string;
  adsenseCookiesLinkText: string;

  // Section 8 — Vos droits (RGPD)
  s8Title: string;
  s8P1: string;
  rightsList: PrivacyLabeledItem[];
  exerciseRightsBold: string;
  exerciseRightsPre: string;
  exerciseRightsPost: string;

  // Section 9 — Conformité Google API Services
  s9Title: string;
  s9Pre: string;
  s9LinkText: string;
  s9Post: string;
  s9List: string[];
  applicablePoliciesLabel: string;
  policyLinks: PrivacyPolicyLink[];

  // Section 10 — Protection des mineurs
  s10Title: string;
  s10Text: string;

  // Section 11 — Contact et réclamations
  s11Title: string;
  contactEmailLabel: string;
  appLabel: string;
  appValue: string;
  gdprComplaintTitle: string;
  competentAuthorityLabel: string;
  competentAuthorityValue: string;
  siteLabel: string;
  footerTextPre: string;
  footerDateLabel: string;
  footerDate: string;
}

export const PRIVACY_CONTENT: Record<'fr' | 'en', PrivacyContent> = {
  fr: {
    backLabel: 'Retour',
    pageTitle: 'Politique de Confidentialité – Jogolinga',
    lastUpdatedLabel: 'Dernière mise à jour :',
    lastUpdatedDate: '28 juin 2026',

    s1Title: '1. Introduction',
    s1P1: "Jogolinga est une plateforme interactive dédiée à l'apprentissage des langues africaines (Wolof, Bambara, Lingala, Peul, Copte, Swahili). Cette politique explique quelles données nous collectons, comment nous les utilisons et comment nous les protégeons.",
    controllerLabel: 'Responsable du traitement :',
    controllerValue: "Den Badji (éditeur de l'application Jogolinga)",
    emailLabel: 'Email :',
    websiteLabel: 'Site web :',

    s2Title: '2. Données collectées via Google',
    s2_1Title: '2.1 Données accédées lors de la connexion',
    s2_1P1: "La connexion à Jogolinga s'effectue exclusivement via Google Sign-In (OAuth 2.0). Nous accédons uniquement aux données suivantes :",
    s2_1List: [
      'Identifiant unique Google (Google User ID)',
      'Nom complet et adresse e-mail',
      'Photo de profil',
      "Tokens d'authentification (access_token, id_token) — stockés temporairement dans le navigateur, supprimés à la déconnexion",
    ],
    limitedScopeLabel: 'Portée limitée',
    limitedScopeText: "Jogolinga utilise uniquement les scopes nécessaires à l'authentification (profil et e-mail). La progression est sauvegardée dans notre base de données Supabase, et non via Google Drive.",
    s2_2Title: '2.2 Données Google non collectées',
    s2_2IntroPre: 'Jogolinga',
    s2_2IntroBold: "n'accède pas",
    s2_2List: [
      'Vos fichiers Google Drive',
      'Vos e-mails Gmail',
      'Votre agenda Google Calendar',
      'Vos contacts Google',
      'Vos photos Google Photos',
      'Tout autre service Google non mentionné dans cette politique',
    ],

    s3Title: '3. Utilisation des données',
    s3_1Title: '3.1 Finalités du traitement',
    authLabel: 'Authentification et identification :',
    authList: [
      'Votre identifiant Google crée et identifie votre compte Jogolinga',
      'Votre nom, pseudo et photo sont affichés dans l\'interface et dans le classement communautaire',
      'Votre e-mail est utilisé pour vous identifier et vous contacter en cas de besoin',
    ],
    progressLabel: 'Sauvegarde de la progression :',
    progressText: "L'ensemble de votre progression d'apprentissage est sauvegardé dans notre base de données cloud Supabase (voir §7 pour la liste détaillée des données). Un cache temporaire est maintenu dans le localStorage de votre navigateur pour permettre un accès hors-ligne immédiat ; ce cache est effacé à la déconnexion.",
    communityLabel: 'Fonctionnalités communautaires :',
    communityText: "Votre pseudo, total d'XP et série quotidienne sont visibles dans le classement des utilisateurs que vous suivez. Ces données sont publiques au sein de la communauté Jogolinga ; vous pouvez choisir un pseudo distinct de votre nom Google.",
    s3_2Title: '3.2 Base légale du traitement',
    legalBasisList: [
      'Votre consentement explicite (connexion via Google)',
      "L'exécution du contrat (fournir les fonctionnalités d'apprentissage promises)",
      "Le respect d'obligations légales, le cas échéant",
    ],
    s3_3Title: '3.3 Durée de conservation',
    retentionList: [
      { label: 'Tokens Google :', text: 'uniquement pendant la session active, supprimés à la déconnexion' },
      { label: 'Cache localStorage :', text: "effacé à la déconnexion ou lors de la connexion d'un autre utilisateur" },
      { label: 'Données de profil et de progression (Supabase) :', text: 'conservées tant que votre compte est actif' },
      { label: 'Données de facturation :', text: 'conservées 10 ans conformément aux obligations légales comptables' },
    ],

    s4Title: '4. Partage des données',
    s4_1Title: '4.1 Sous-traitants',
    subprocessorSupabaseName: 'Supabase :',
    subprocessorSupabaseText: 'Hébergement de la base de données (PostgreSQL, serveurs EU, conforme RGPD). Stockage de toutes les données de progression, profil et abonnement. Supabase peut enregistrer des données techniques de connexion (adresse IP, horodatage) à des fins de sécurité.',
    subprocessorGoogleName: 'Google LLC :',
    subprocessorGoogleText1: "Traitement de l'authentification OAuth 2.0. Nous utilisons également",
    subprocessorGoogleAdSenseBold: 'Google AdSense',
    subprocessorGoogleText2: "pour afficher des annonces publicitaires aux utilisateurs non abonnés (version gratuite). Google AdSense peut déposer des cookies et utiliser des technologies similaires pour personnaliser les annonces en fonction de vos visites. Vous pouvez contrôler la personnalisation des annonces via",
    subprocessorGoogleLinkText: 'les paramètres publicitaires Google',
    subprocessorStripeName: 'Stripe :',
    subprocessorStripeText: "Traitement des paiements de l'abonnement Premium (conforme PCI-DSS et RGPD). Jogolinga ne stocke aucune donnée bancaire.",
    subprocessorVercelName: 'Vercel :',
    subprocessorVercelText: 'Hébergement du frontend (conforme RGPD).',
    noCommercialLabel: 'Aucun tiers commercial :',
    noCommercialText: 'Nous ne vendons, ne louons ni ne partageons vos données à des fins publicitaires ou commerciales.',
    s4_2Title: '4.2 Partage obligatoire',
    s4_2Intro: 'Nous pouvons divulguer vos données uniquement si :',
    mandatorySharingList: [
      'Requis par la loi ou une procédure judiciaire',
      'Nécessaire pour protéger nos droits légaux',
      'Requis pour prévenir une fraude ou un danger immédiat',
    ],

    s5Title: '5. Sécurité des données',
    s5List: [
      'Toutes les communications utilisent HTTPS/TLS',
      "Authentification via OAuth 2.0 (Google) — aucun mot de passe n'est stocké par Jogolinga",
      'Accès à la base de données restreint par Row Level Security (RLS) Supabase : chaque utilisateur ne peut accéder qu\'à ses propres données',
      "Tokens d'accès à durée de vie limitée, supprimés à la déconnexion",
    ],

    s6Title: '6. Suppression des données',
    s6P1: "Vous pouvez réinitialiser votre progression ou supprimer complètement votre compte à tout moment, directement depuis l'application — aucune démarche par e-mail n'est nécessaire. La suppression du compte est immédiate : vos données de progression, d'abonnement, de profil et de connexion sont effacées de notre base Supabase dès la confirmation. Les données de facturation liées à Stripe sont conservées séparément pendant 10 ans, conformément aux obligations légales comptables.",
    s6P2: 'Depuis votre profil, dans la zone « Danger » :',
    s6Steps: [
      "« Réinitialiser ma progression » efface uniquement vos données d'apprentissage (mots appris, XP, série) — votre compte et votre abonnement restent actifs",
      "« Supprimer mon compte » efface définitivement le compte et l'ensemble des données associées, après saisie d'un mot de confirmation dans la fenêtre dédiée",
      'Aucune des deux actions ne nécessite de nous contacter : la demande est traitée immédiatement dans l\'application',
    ],
    s6NotePre: 'Ces deux actions sont',
    s6NoteBold: 'immédiates et irréversibles',

    s7Title: "7. Données d'application collectées",
    s7_1Title: '7.1 Données de progression (privées)',
    s7_1List: [
      'Mots de vocabulaire appris par catégorie et langue',
      'Règles de grammaire maîtrisées',
      'Phrases construites et maîtrisées',
      'Catégories et unités complétées',
      "Total de points d'expérience (XP) et niveau atteint (1 à 12)",
      "Série quotidienne (streak) et historique d'activité des 30 derniers jours",
      'Statistiques de révision (mots à revoir, scores)',
      'Plan d\'abonnement (gratuit ou Premium)',
    ],
    s7_2Title: '7.2 Données de profil communautaire (visibles par les autres utilisateurs)',
    s7_2List: [
      "Pseudo choisi par l'utilisateur",
      'XP total et série quotidienne (visibles dans le classement)',
      'Liste des utilisateurs suivis et abonnés',
    ],
    s7_3Title: '7.3 Données techniques',
    s7_3Item1: 'Supabase enregistre automatiquement les adresses IP et horodatages de connexion à des fins de sécurité — ces données ne sont pas exploitées par Jogolinga',
    adsenseCookiesBold: 'Cookies publicitaires Google AdSense :',
    adsenseCookiesText: "utilisés uniquement pour les utilisateurs de la version gratuite afin d'afficher des annonces pertinentes. Les utilisateurs abonnés Premium ne sont pas soumis à ces cookies. Vous pouvez désactiver la personnalisation sur",
    adsenseCookiesLinkText: 'google.com/settings/ads',

    s8Title: '8. Vos droits (RGPD)',
    s8P1: 'Conformément au RGPD, vous disposez des droits suivants :',
    rightsList: [
      { label: 'Accès :', text: 'obtenir une copie de vos données' },
      { label: 'Rectification :', text: 'corriger vos informations inexactes' },
      { label: 'Effacement (« droit à l\'oubli ») :', text: 'demander la suppression de vos données' },
      { label: 'Portabilité :', text: 'recevoir vos données dans un format lisible (JSON)' },
      { label: 'Opposition :', text: 'refuser certains traitements' },
      { label: 'Limitation :', text: 'demander la suspension temporaire du traitement' },
    ],
    exerciseRightsBold: 'Pour exercer vos droits :',
    exerciseRightsPre: 'envoyez un e-mail à',
    exerciseRightsPost: 'avec l\'objet « Demande RGPD ». Nous répondrons dans un délai de 30 jours.',

    s9Title: '9. Conformité avec la Google API Services User Data Policy',
    s9Pre: 'Jogolinga respecte la',
    s9LinkText: 'Politique de données utilisateur Google API Services',
    s9Post: ', notamment :',
    s9List: [
      "Les données Google sont utilisées uniquement pour l'authentification et l'identification de l'utilisateur",
      "Aucune donnée Google n'est transmise à des tiers, sauf si requis par la loi",
      "Les données personnelles de l'utilisateur (nom, e-mail, progression) ne sont pas utilisées à des fins publicitaires ciblées — les annonces AdSense se basent sur le contexte de navigation, non sur votre profil Jogolinga",
      'Consentement explicite requis avant tout accès',
    ],
    applicablePoliciesLabel: 'Politiques applicables :',
    policyLinks: [
      { text: 'Politique de confidentialité Google', href: 'https://policies.google.com/privacy' },
      { text: 'Politique de données utilisateur Google API Services', href: 'https://developers.google.com/terms/api-services-user-data-policy' },
    ],

    s10Title: '10. Protection des mineurs',
    s10Text: "Jogolinga n'est pas destinée aux enfants de moins de 13 ans. Les utilisateurs âgés de 13 à 18 ans doivent obtenir le consentement d'un parent ou tuteur légal avant d'utiliser l'application.",

    s11Title: '11. Contact et réclamations',
    contactEmailLabel: 'Email :',
    appLabel: 'Application :',
    appValue: 'Jogolinga',
    gdprComplaintTitle: 'Réclamation RGPD :',
    competentAuthorityLabel: 'Autorité compétente :',
    competentAuthorityValue: "CNIL (Commission Nationale de l'Informatique et des Libertés)",
    siteLabel: 'Site :',
    footerTextPre: 'Cette politique peut être mise à jour périodiquement. En cas de changement majeur, vous serez informé par e-mail ou notification dans l\'application.',
    footerDateLabel: 'Date de dernière modification :',
    footerDate: '28 juin 2026',
  },
  en: {
    backLabel: 'Back',
    pageTitle: 'Privacy Policy – Jogolinga',
    lastUpdatedLabel: 'Last updated:',
    lastUpdatedDate: 'June 28, 2026',

    s1Title: '1. Introduction',
    s1P1: 'Jogolinga is an interactive platform dedicated to learning African languages (Wolof, Bambara, Lingala, Fula, Coptic, Swahili). This policy explains what data we collect, how we use it, and how we protect it.',
    controllerLabel: 'Data controller:',
    controllerValue: 'Den Badji (publisher of the Jogolinga application)',
    emailLabel: 'Email:',
    websiteLabel: 'Website:',

    s2Title: '2. Data collected via Google',
    s2_1Title: '2.1 Data accessed at sign-in',
    s2_1P1: 'Signing in to Jogolinga is done exclusively via Google Sign-In (OAuth 2.0). We only access the following data:',
    s2_1List: [
      'Unique Google identifier (Google User ID)',
      'Full name and email address',
      'Profile photo',
      'Authentication tokens (access_token, id_token) — stored temporarily in the browser, deleted upon sign-out',
    ],
    limitedScopeLabel: 'Limited scope',
    limitedScopeText: 'Jogolinga only uses the scopes required for authentication (profile and email). Your progress is saved in our Supabase database, not via Google Drive.',
    s2_2Title: '2.2 Google data we do not collect',
    s2_2IntroPre: 'Jogolinga',
    s2_2IntroBold: 'does not access',
    s2_2List: [
      'Your Google Drive files',
      'Your Gmail emails',
      'Your Google Calendar',
      'Your Google contacts',
      'Your Google Photos',
      'Any other Google service not mentioned in this policy',
    ],

    s3Title: '3. Use of data',
    s3_1Title: '3.1 Purposes of processing',
    authLabel: 'Authentication and identification:',
    authList: [
      'Your Google identifier creates and identifies your Jogolinga account',
      'Your name, username and photo are displayed in the interface and in the community leaderboard',
      'Your email is used to identify you and to contact you if needed',
    ],
    progressLabel: 'Saving your progress:',
    progressText: 'All of your learning progress is saved in our Supabase cloud database (see §7 for the detailed list of data). A temporary cache is kept in your browser\'s localStorage to allow immediate offline access; this cache is cleared upon sign-out.',
    communityLabel: 'Community features:',
    communityText: 'Your username, total XP and daily streak are visible in the leaderboard of the users you follow. This data is public within the Jogolinga community; you may choose a username different from your Google name.',
    s3_2Title: '3.2 Legal basis for processing',
    legalBasisList: [
      'Your explicit consent (signing in via Google)',
      'Performance of the contract (providing the promised learning features)',
      'Compliance with legal obligations, where applicable',
    ],
    s3_3Title: '3.3 Retention period',
    retentionList: [
      { label: 'Google tokens:', text: 'only for the duration of the active session, deleted upon sign-out' },
      { label: 'localStorage cache:', text: 'cleared upon sign-out or when another user signs in' },
      { label: 'Profile and progress data (Supabase):', text: 'retained for as long as your account is active' },
      { label: 'Billing data:', text: 'retained for 10 years in accordance with legal accounting obligations' },
    ],

    s4Title: '4. Data sharing',
    s4_1Title: '4.1 Processors',
    subprocessorSupabaseName: 'Supabase:',
    subprocessorSupabaseText: 'Hosts the database (PostgreSQL, EU servers, GDPR-compliant). Stores all progress, profile and subscription data. Supabase may log technical connection data (IP address, timestamp) for security purposes.',
    subprocessorGoogleName: 'Google LLC:',
    subprocessorGoogleText1: 'Handles OAuth 2.0 authentication. We also use',
    subprocessorGoogleAdSenseBold: 'Google AdSense',
    subprocessorGoogleText2: 'to display advertisements to non-subscribed users (free version). Google AdSense may set cookies and use similar technologies to personalize ads based on your visits. You can control ad personalization via',
    subprocessorGoogleLinkText: 'Google ad settings',
    subprocessorStripeName: 'Stripe:',
    subprocessorStripeText: 'Handles payments for the Premium subscription (PCI-DSS and GDPR compliant). Jogolinga does not store any banking data.',
    subprocessorVercelName: 'Vercel:',
    subprocessorVercelText: 'Hosts the frontend (GDPR-compliant).',
    noCommercialLabel: 'No commercial third parties:',
    noCommercialText: 'We do not sell, rent, or share your data for advertising or commercial purposes.',
    s4_2Title: '4.2 Mandatory disclosure',
    s4_2Intro: 'We may disclose your data only if:',
    mandatorySharingList: [
      'Required by law or legal proceedings',
      'Necessary to protect our legal rights',
      'Required to prevent fraud or an immediate danger',
    ],

    s5Title: '5. Data security',
    s5List: [
      'All communications use HTTPS/TLS',
      'Authentication via OAuth 2.0 (Google) — no password is stored by Jogolinga',
      'Database access restricted by Supabase Row Level Security (RLS): each user can only access their own data',
      'Access tokens have a limited lifetime and are deleted upon sign-out',
    ],

    s6Title: '6. Data deletion',
    s6P1: "You can reset your progress or delete your account entirely at any time, directly from the app — no need to email us. Account deletion is immediate: your progress, subscription, profile and login data are erased from our Supabase database as soon as you confirm. Stripe billing data is kept separately for 10 years, in line with legal accounting requirements.",
    s6P2: 'From your profile, in the "Danger zone":',
    s6Steps: [
      '"Reset my progress" only erases your learning data (words learned, XP, streak) — your account and subscription stay active',
      '"Delete my account" permanently erases the account and all associated data, after you confirm in the dedicated dialog',
      'Neither action requires contacting us: the request is processed immediately in the app',
    ],
    s6NotePre: 'Both actions are',
    s6NoteBold: 'immediate and irreversible',

    s7Title: '7. Application data collected',
    s7_1Title: '7.1 Progress data (private)',
    s7_1List: [
      'Vocabulary words learned by category and language',
      'Grammar rules mastered',
      'Sentences built and mastered',
      'Categories and units completed',
      'Total experience points (XP) and level reached (1 to 12)',
      'Daily streak and 30-day activity history',
      'Review statistics (words to review, scores)',
      'Subscription plan (free or Premium)',
    ],
    s7_2Title: '7.2 Community profile data (visible to other users)',
    s7_2List: [
      'Username chosen by the user',
      'Total XP and daily streak (visible in the leaderboard)',
      'List of followed and follower users',
    ],
    s7_3Title: '7.3 Technical data',
    s7_3Item1: 'Supabase automatically logs IP addresses and connection timestamps for security purposes — this data is not used by Jogolinga',
    adsenseCookiesBold: 'Google AdSense advertising cookies:',
    adsenseCookiesText: 'used only for free-version users to display relevant ads. Premium subscribers are not subject to these cookies. You can disable personalization at',
    adsenseCookiesLinkText: 'google.com/settings/ads',

    s8Title: '8. Your rights (GDPR)',
    s8P1: 'In accordance with the GDPR, you have the following rights:',
    rightsList: [
      { label: 'Access:', text: 'obtain a copy of your data' },
      { label: 'Rectification:', text: 'correct inaccurate information' },
      { label: 'Erasure ("right to be forgotten"):', text: 'request the deletion of your data' },
      { label: 'Portability:', text: 'receive your data in a readable format (JSON)' },
      { label: 'Objection:', text: 'object to certain processing' },
      { label: 'Restriction:', text: 'request the temporary suspension of processing' },
    ],
    exerciseRightsBold: 'To exercise your rights:',
    exerciseRightsPre: 'send an email to',
    exerciseRightsPost: 'with the subject line "GDPR Request". We will respond within 30 days.',

    s9Title: '9. Compliance with the Google API Services User Data Policy',
    s9Pre: 'Jogolinga complies with the',
    s9LinkText: 'Google API Services User Data Policy',
    s9Post: ', in particular:',
    s9List: [
      "Google data is used solely for user authentication and identification",
      'No Google data is transmitted to third parties, except as required by law',
      "The user's personal data (name, email, progress) is not used for targeted advertising purposes — AdSense ads are based on browsing context, not on your Jogolinga profile",
      'Explicit consent is required before any access',
    ],
    applicablePoliciesLabel: 'Applicable policies:',
    policyLinks: [
      { text: 'Google Privacy Policy', href: 'https://policies.google.com/privacy' },
      { text: 'Google API Services User Data Policy', href: 'https://developers.google.com/terms/api-services-user-data-policy' },
    ],

    s10Title: '10. Protection of minors',
    s10Text: 'Jogolinga is not intended for children under the age of 13. Users aged 13 to 18 must obtain the consent of a parent or legal guardian before using the application.',

    s11Title: '11. Contact and complaints',
    contactEmailLabel: 'Email:',
    appLabel: 'Application:',
    appValue: 'Jogolinga',
    gdprComplaintTitle: 'GDPR complaint:',
    competentAuthorityLabel: 'Competent authority:',
    competentAuthorityValue: 'CNIL (French Data Protection Authority – Commission Nationale de l\'Informatique et des Libertés)',
    siteLabel: 'Website:',
    footerTextPre: 'This policy may be updated periodically. In the event of a major change, you will be notified by email or in-app notification.',
    footerDateLabel: 'Last modification date:',
    footerDate: 'June 28, 2026',
  },
};
